import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spinner } from '@kollektive/design-system';
import { useAuth } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { RETURN_URL_KEY } from '@/routes/AuthCallback';
import { AccessDenied } from '@/routes/AccessDenied';
import styles from './ProtectedRoute.module.css';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Wrapper component that protects routes requiring authentication.
 * Redirects to login if user is not authenticated.
 * Shows access denied if user is not a member of the current tenant.
 * Stores the current URL for redirect after login.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const { tenant } = useTenant();
  const { currentUser, isLoading: isUserLoading, error: userError } = useCurrentUser();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Spinner size="lg" />
          <p className={styles.text}>Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Store the current URL so we can redirect back after login
    const returnUrl = location.pathname + location.search;
    sessionStorage.setItem(RETURN_URL_KEY, returnUrl);

    return <Navigate to="/login" replace />;
  }

  // Show loading while checking tenant membership
  if (isUserLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Spinner size="lg" />
          <p className={styles.text}>Loading...</p>
        </div>
      </div>
    );
  }

  // If /api/v1/me resolved but user is not a member of this tenant, show access denied.
  // Only show this when we have a definitive answer (no error, data loaded).
  if (!userError && !isUserLoading && tenant && !currentUser) {
    return <AccessDenied tenantName={tenant.name} />;
  }

  // User is authenticated, render children
  return <>{children}</>;
}
